import { memo, useEffect, useMemo, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import {
  Form,
  Button,
  TextFieldControlled as Input,
  ToggleFieldControlled as Toggle,
  SelectFieldMinimal,
  FieldWrap,
} from '@leafwell/components';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useEventsTracker } from 'contexts/eventsTracker';
import { StatesListItem, useStatesList } from 'hooks/useStatesList';
import { setStateCookie } from 'lib/cookies/state-cookie';
import { useRouter } from 'next/router';
import { useMedicalCardParams } from 'hooks/useMedicalCardParams';
import { clientId } from 'lib/tracking/gtag';

const MEMBERSHIP_ONLY_STATE_CODE = 'fl';

declare module 'react' {
  interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
    before?: string;
  }
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

type SignupFormSimpleProps = {
  className?: string;
  formSubmitText?: string;
  stateCode: string;
  promoCode?: string | null;
  showBorder?: boolean;
  ctaLocation?: string;
  showStateSelect?: boolean;
};

const SignupFormSimple: React.FC<SignupFormSimpleProps> = memo(
  ({
    stateCode: initialStateCode,
    className,
    formSubmitText,
    showBorder = true,
    ctaLocation,
    showStateSelect = true,
    promoCode = null,
  }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const statesList = useStatesList(false);

    const { trackEvent } = useEventsTracker();
    const { translations, cardPage, statePages } = useWordPressContext();

    const baseUrl = process.env.NEXT_PUBLIC_PRODUCT_ENDPOINT;
    const medicalCardParams = useMedicalCardParams();
    const [discountCode, setDiscountCode] = useState<string>(promoCode ?? null);
    const { query } = useRouter();
    const posthog = usePostHog();
    const [fpromTid, setFpromTid] = useState<string | null>(null);
    const [fpromRefId, setFpromRefId] = useState<string | null>(null);
    const [sessionId, setSessionId] = useState<string>('');
    const [distinctId, setDistinctId] = useState<string>('');

    const medicalCardParamsString = useMemo(() => {
      if (!medicalCardParams || typeof medicalCardParams !== 'object')
        return '';
      return Object.entries(medicalCardParams)
        .filter(([, value]) => value != null && value !== '')
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
        )
        .join('&');
    }, [medicalCardParams]);

    useEffect(() => {
      setFpromTid(getCookie('_fprom_tid'));
      setFpromRefId(getCookie('_fprom_ref'));
    }, []);

    useEffect(() => {
      setSessionId(posthog.get_session_id() ?? '');
      setDistinctId(posthog.get_distinct_id() ?? '');
    }, [posthog]);

    useEffect(() => {
      if (query.discount_code && query.discount_code != '') {
        setDiscountCode(query.discount_code.toString().toUpperCase());
      }
    }, [query]);

    const selectedStateObject: StatesListItem = useMemo(
      () => statesList.find(item => item.value === initialStateCode),
      [statesList, initialStateCode],
    );

    const handleSelectState = (stateCode?: string) => {
      const hasStateCode = stateCode && stateCode !== '';
      const url = hasStateCode
        ? statePages.find(({ code }) => code === stateCode)?.uri
        : cardPage?.uri;

      if (hasStateCode) {
        setStateCookie(stateCode);
      }

      if (url) {
        window.location.assign(url);
      }
    };

    const onSubmit = async ({ email, accepted_terms_of_service }) => {
      if (!email || !accepted_terms_of_service) {
        setIsLoading(false);
        console.error('Please fill in all fields');
        return;
      } else {
        setIsLoading(true);
      }

      const referrerData = medicalCardParams?.utm_referrer
        ? { page_referrer: medicalCardParams.utm_referrer }
        : {};

      const discountCodeData = discountCode
        ? { discount_code: discountCode }
        : {};

      const ctaLocationData = ctaLocation ? { cta_location: ctaLocation } : {};

      trackEvent({
        event: 'generate_lead',
        service: 'medical_card',
        location: translations[initialStateCode.toUpperCase()],
        client_id: clientId(),
        ...referrerData,
        ...discountCodeData,
        ...ctaLocationData,
      });

      const isMembershipState =
        initialStateCode?.toLowerCase() === MEMBERSHIP_ONLY_STATE_CODE;
      const targetBaseUrl = isMembershipState
        ? `${process.env.NEXT_PUBLIC_LOGIN_URL}/membership`
        : baseUrl;

      const href = `${targetBaseUrl}?${medicalCardParamsString}&session_id=${sessionId}&distinct_id=${distinctId}&state_code=${initialStateCode?.toLowerCase()}&email=${email}${
        discountCode ? `&discount_code=${discountCode}` : ''
      }${fpromRefId ? `&fp_ref=${fpromRefId}` : ''}${
        fpromTid ? `&fp_tid=${fpromTid}` : ''
      }`;
      window.location.assign(href);
      setIsLoading(false);
    };

    return (
      <div
        className={`grid transition-opacity duration-500 gap-4 p-4 ${
          showBorder ? 'bg-[#F6FEF9] border border-green-500' : ''
        } rounded-lg w-fit ${className}`}
      >
        <Form
          className="md:gap-x-3 gap-y-4"
          mode="onSubmit"
          onSubmit={onSubmit}
          defaultValues={{
            stateCode: initialStateCode,
            email: '',
            accepted_terms_of_service: false,
          }}
        >
          {showStateSelect ? (
            <div className="grid gap-y-2 js-form-field relative">
              <p className="font-medium">Select your state</p>
              <FieldWrap name="stateCode">
                <SelectFieldMinimal
                  aria-label="Select your state"
                  defaultValue={selectedStateObject}
                  placeholder={translations['Select State']}
                  options={statesList}
                  onSelect={target => handleSelectState(target.value)}
                />
              </FieldWrap>
            </div>
          ) : null}
          <div className="grid gap-y-2 js-form-field relative">
            <p className="font-medium">
              Enter your email <sup>*</sup>
            </p>
            <Input
              className="rounded-lg"
              name="email"
              placeholder="Email *"
              type="email"
              validations={['required', 'email']}
            />
          </div>
          <div className="grid gap-y-2 js-form-field relative">
            <Toggle
              className="border-gray-300"
              type="checkbox"
              name="accepted_terms_of_service"
              validations={['required']}
              options={[
                {
                  label: (
                    <p className="text-sm">
                      {translations['I have read and accept the']}{' '}
                      <a
                        target="_blank"
                        href="https://leafwell.com/terms-of-use"
                      >
                        {translations['terms of service']}
                      </a>{' '}
                      {translations['and']}{' '}
                      <a
                        target="_blank"
                        href="https://leafwell.com/privacy-policy"
                      >
                        {translations['privacy policy']}
                      </a>
                    </p>
                  ),
                  value: '1',
                },
              ]}
            />
          </div>
          <div className="mt-2 w-full">
            <Button
              type="submit"
              label={formSubmitText}
              variant="primary"
              name="commit"
              id="submitt_embeded_step_1"
              className="w-full rounded-lg"
              loading={isLoading}
              data-disable-with={formSubmitText}
            />
          </div>
        </Form>
      </div>
    );
  },
);

export default SignupFormSimple;
